import { getTableIntentLink } from "@components/drillDown/DrillDown.utils";
import { isValidDate } from "@components/inputs/date/utils";
import { sortItemsByPredefinedOrder } from "@components/inputs/select/SelectAPI";
import { SwitchType } from "@components/inputs/switch/Switch";
import {
    enhanceFieldVisibility,
    getHoursUnit,
    ifAll,
    IFieldDef,
    IFieldInfoProperties,
    not,
    TGetValueFn,
    TInfoValue,
    UnavailableActionStyle
} from "@components/smart/FieldInfo";
import {
    AttachmentsDef,
    BirthNumberFieldDef,
    getCommonFilterDefs,
    withDisplayName
} from "@components/smart/GeneralFieldDefinition";
import SmartFastEntryList, { ICustomRowAfterContentArgs } from "@components/smart/smartFastEntryList";
import { SmartFastEntrySubListWrapper } from "@components/smart/smartFastEntryList/SmartFastEntryList.styles";
import { formAttachmentFieldDef } from "@components/smart/smartFileInput/SmartFileInput.utils";
import {
    FilterBarGroup,
    getDefaultFilterGroupDef,
    IFilterGroupDef
} from "@components/smart/smartFilterBar/SmartFilterBar.types";
import { IFormGroupDef } from "@components/smart/smartFormGroup/SmartFormGroup";
import { ICellValueObject } from "@components/table";
import { createPath, SEPARATOR } from "@odata/BindingContext";
import { UserPermissionCodes } from "@odata/CompanyPermissionEnums";
import {
    EntitySetName,
    EntityTypeName,
    IPrCaregivingEventEntity,
    IPrSocialEventEntity,
    IPrSocialEventMonthDetailEntity,
    IPrSocialEventMonthIntervalEntity,
    PrCaregivingEventEntity,
    PrCareReceiverEntity,
    PrCareReceiverSocialEventEntity,
    PrEmployeeDependentPersonEntity,
    PrEmployeeEntity,
    PrEmploymentEntity,
    PrLongTermCaregivingEventEntity,
    PrMaternityLeaveEventEntity,
    PrMpsvRelationEntity,
    PrPaternityLeaveEventEntity,
    PrSocialEventEntity,
    PrSocialEventMonthDetailEntity,
    PrSocialEventMonthDetailTypeEntity,
    PrSocialEventMonthIntervalEntity,
    PrSubmissionEntity
} from "@odata/GeneratedEntityTypes";
import {
    PrMpsvFamilyRelationCode,
    PrMpsvRelationCode,
    PrSocialEventMonthDetailTypeCode,
    PrSocialEventStatusCode,
    PrSocialEventTypeCode,
    PrSubmissionTypeCode
} from "@odata/GeneratedEnums";
import { getEnumNameSpaceName } from "@odata/GeneratedEnums.utils";
import { IFormatOptions } from "@odata/OData.utils";
import {
    employeeWithDependentEmploymentSelectDef,
    getEmployeeFilterDef,
    getEmployeeSummaryItemDef,
    getEmployeeTableColumnDefs,
    getEmploymentSelectDef,
    getIdentificationTypeSelectDef,
    IdentificationType,
    IdentificationTypeLocalPath,
    isOfIdentType
} from "@pages/payroll/employee/Employee.utils";
import { SocialEventGroup } from "@pages/payroll/socialEvents/SocialEvents.types";
import {
    canChangeEmployee,
    dateEventEndLocalPath,
    employeeDependentsLocalPath,
    filterOutInvisibleDurationItems,
    getFilterForTabId,
    getMonthIntervalDay,
    getMonthIntervalDayStorage,
    getOtherPropertyGetter,
    getSingleEmployeeSocialEventFilter,
    getSocialEventsEntityType,
    getSocialEventStatusColor,
    getSocialEventStatusLabel,
    getSocialEventTypeLabel,
    hasDurationIntervalsLocalPath,
    isAllowedToCreateSubmission,
    isDurationGroupToggled,
    seT,
    showEmployeeDependentsSwitchLocalPath,
    SocialEventTabStatus,
    TSocialEventFormStorage,
    workedHoursOnLastDayLocalPath
} from "@pages/payroll/socialEvents/SocialEvents.utils";
import { getSubmissionTableDefinitionForFormTabs } from "@pages/payroll/submission/GetSubmissionTableDefinitionForFormTabs";
import {
    getNempriSubmissionSubTypeFromSocialEventType,
    PrSubmissionEnumTranslationNamespaces
} from "@pages/payroll/submission/Submission.utils";
import { isNewEntity } from "@utils/definition.utils";
import { capitalize } from "@utils/string";
import { Dayjs } from "dayjs";
import i18next from "i18next";
import React, { ComponentType } from "react";

import { DASH_CHARACTER } from "../../../constants";
import { IAppContext } from "../../../contexts/appContext/AppContext.types";
import {
    BasicInputSizes,
    FastEntryInputSizes,
    FieldType,
    GroupedField,
    LabelStatus,
    NavigationSource,
    Sort,
    TextAlign,
    ValidatorType
} from "../../../enums";
import { ColoredText } from "../../../global.style";
import { TValue } from "../../../global.types";
import { Model } from "../../../model/Model";
import { ROUTE_EMPLOYMENT } from "../../../routes";
import DateType, { DATE_MIN, DateFormat, getUtcDayjs } from "../../../types/Date";
import { IFormDef } from "../../../views/formView/Form";
import { addFormDef, addFormGroup, getFormGroup } from "../../../views/formView/Form.utils";
import { FormStorage } from "../../../views/formView/FormStorage";
import { ISplitPageTableDef, SECONDARY_FILTERS_ALL } from "../../../views/table/TableView.utils";
import { setDefByEntityType } from "../../getDefByEntityType";
import { getNumberOursSummaryDef, getNumberRangeFieldDefs } from "../../numberRange/NumberRange.utils";
import { getItemBreadCrumbsText, IDefinition, IGetDefinition } from "../../PageUtils";

export function isOfCaregivingDetailType(type: PrSocialEventMonthDetailTypeCode): TGetValueFn<boolean> {
    return ({ storage, bindingContext }) => {
        const isLongTerm = storage.data.bindingContext.getEntityType().getName() === EntityTypeName.PrLongTermCaregivingEvent;
        const BaseEntity = isLongTerm ? PrLongTermCaregivingEventEntity : PrCaregivingEventEntity;
        while (bindingContext && bindingContext.getPath(true) !== BaseEntity.MonthDetails) {
            bindingContext = bindingContext.getParent();
        }
        if (!bindingContext) {
            return false;
        }
        const bc = bindingContext.navigate(PrSocialEventMonthDetailEntity.MonthDetailType);
        const current = storage.getValue(bc, { useDirectValue: false });
        return current === type;
    };
}

const isEmployeeDependentsSwitchOn: TGetValueFn<boolean> = (args) => {
    const bc = args.bindingContext.getParent().navigate(showEmployeeDependentsSwitchLocalPath);
    return args.storage.getValue(bc) === true;
};

/**
 * Adds common Care Receiver field definitions, just returns care receiver rows to be extended and added to group
 * @param d
 * @param propName
 * @param prefixRows
 * @param addEmployeeRelation
 */
export function addCareReceiverDefinitions(d: IDefinition, propName: string, prefixRows = true, addEmployeeRelation = false): IFieldDef[][] {
    addFormDef(d, createPath(propName, PrCareReceiverEntity.FirstName), {
        groupedField: GroupedField.MultiStart,
        isVisible: addEmployeeRelation ? not(isEmployeeDependentsSwitchOn) : undefined,
        clearIfInvisible: !addEmployeeRelation
    });
    addFormDef(d, createPath(propName, PrCareReceiverEntity.LastName), {
        groupedField: GroupedField.MultiEnd,
        isVisible: addEmployeeRelation ? not(isEmployeeDependentsSwitchOn) : undefined,
        clearIfInvisible: !addEmployeeRelation
    });
    addFormDef(d, createPath(propName, IdentificationTypeLocalPath), {
        ...getIdentificationTypeSelectDef([IdentificationType.EuSocialInsuranceNumber]),
        groupedField: GroupedField.MultiStart,
        affectedFields: [
            { id: PrCareReceiverEntity.DateOfBirth, navigateFromParent: true },
            { id: PrCareReceiverEntity.IdentificationNumber, navigateFromParent: true }
        ],
        isVisible: addEmployeeRelation ? not(isEmployeeDependentsSwitchOn) : undefined,
        clearIfInvisible: !addEmployeeRelation
    });
    addFormDef(d, createPath(propName, PrCareReceiverEntity.DateOfBirth), {
        labelStatus: LabelStatus.Hidden,
        groupedField: GroupedField.MultiEnd,
        isRequired: true,
        isVisible: addEmployeeRelation ? ifAll(not(isEmployeeDependentsSwitchOn), isOfIdentType(IdentificationType.DateOfBirth)) : isOfIdentType(IdentificationType.DateOfBirth),
        clearIfInvisible: !addEmployeeRelation,
    });
    addFormDef(d, createPath(propName, PrCareReceiverEntity.IdentificationNumber), {
        ...BirthNumberFieldDef,
        labelStatus: LabelStatus.Hidden,
        groupedField: GroupedField.MultiEnd,
        isRequired: true,
        isVisible: addEmployeeRelation ? ifAll(not(isEmployeeDependentsSwitchOn), isOfIdentType(IdentificationType.IdentificationNumber)) : isOfIdentType(IdentificationType.IdentificationNumber),
        clearIfInvisible: !addEmployeeRelation
    });

    if (addEmployeeRelation) {
        // For Caregiving events, we want to be able to predefine fields from Employee data. Add switch + select
        // with filled rows from Employee->Dependents. If user selects one, we will fill the CareReceiver fields with data from Employee->Dependent.
        addFormDef(d, createPath(propName, showEmployeeDependentsSwitchLocalPath), {
            type: FieldType.Switch,
            label: i18next.t("SocialEvents:Form.ShowEmployeeDependents"),
            fieldSettings: {
                type: SwitchType.YesNo,
            },
            isVisible: (args) => !(args.storage as FormStorage).isReadOnly,
            isDisabled: (args) =>
                !(args.storage.data.entity as IPrCaregivingEventEntity).Employee?.Dependents?.length,
            affectedFields: [
                { id: PrCareReceiverEntity.FirstName, navigateFromParent: true },
                { id: PrCareReceiverEntity.LastName, navigateFromParent: true },
                { id: IdentificationTypeLocalPath, navigateFromParent: true },
                { id: PrCareReceiverEntity.DateOfBirth, navigateFromParent: true },
                { id: PrCareReceiverEntity.IdentificationNumber, navigateFromParent: true },
                { id: employeeDependentsLocalPath, navigateFromParent: true },
            ]
        });

        addFormDef(d, createPath(propName, employeeDependentsLocalPath), {
            type: FieldType.ComboBox,
            label: i18next.t("SocialEvents:Form.EmployeeDependents"),
            isVisible: isEmployeeDependentsSwitchOn,
            width: BasicInputSizes.L,
            fieldSettings: {
                shouldDisplayAdditionalColumns: true,
                localDependentFields: [{
                    from: { id: PrEmployeeDependentPersonEntity.FirstName },
                    to: { id: PrCareReceiverEntity.FirstName },
                    navigateFrom: NavigationSource.Parent
                }, {
                    from: { id: PrEmployeeDependentPersonEntity.LastName },
                    to: { id: PrCareReceiverEntity.LastName },
                    navigateFrom: NavigationSource.Parent
                }, {
                    from: { id: PrEmployeeDependentPersonEntity.PersonalIdentificationNumber },
                    to: { id: PrCareReceiverEntity.IdentificationNumber },
                    navigateFrom: NavigationSource.Parent
                }, {
                    from: { id: PrEmployeeDependentPersonEntity.DateOfBirth },
                    to: { id: PrCareReceiverEntity.DateOfBirth },
                    navigateFrom: NavigationSource.Parent
                }],
            },
            columns: [{
                id: PrCareReceiverEntity.LastName,
            }, {
                id: PrCareReceiverEntity.DateOfBirth,
            }],
        });

        d.form.additionalProperties.push({
            id: PrSocialEventEntity.Employee,
            additionalProperties: [
                {
                    id: PrEmployeeEntity.Dependents,
                    additionalProperties: [
                        { id: PrEmployeeDependentPersonEntity.FirstName },
                        { id: PrEmployeeDependentPersonEntity.LastName },
                        { id: PrEmployeeDependentPersonEntity.DateOfBirth },
                        { id: PrEmployeeDependentPersonEntity.PersonalIdentificationNumber },
                        { id: PrEmployeeDependentPersonEntity.RelationCode },
                    ]
                }
            ]
        });
    }

    if (!prefixRows) {
        // For CareReceiver in collection, we must not prefix row paths
        propName = "";
    }

    return [
        (addEmployeeRelation ? [
            { id: createPath(propName, showEmployeeDependentsSwitchLocalPath) }
        ] : []),
        [
        { id: createPath(propName, PrCareReceiverEntity.FirstName) },
        { id: createPath(propName, PrCareReceiverEntity.LastName) }
    ], [
        { id: createPath(propName, IdentificationTypeLocalPath) },
        { id: createPath(propName, PrCareReceiverEntity.DateOfBirth) },
        { id: createPath(propName, PrCareReceiverEntity.IdentificationNumber) }
        ],
        (addEmployeeRelation ? [
            { id: createPath(propName, employeeDependentsLocalPath) }
        ] : [])
    ].filter(item => item.length > 0) as IFieldDef[][];
}

export const MpsvRelationCodeOrderTransformation = sortItemsByPredefinedOrder([
    PrMpsvRelationCode.Spouse,
    PrMpsvRelationCode.PartnerOfCareReceiver,
    PrMpsvRelationCode.Son_Daugther,
    PrMpsvRelationCode.Father_Mother,
    PrMpsvRelationCode.Brother_Sister,
    PrMpsvRelationCode.Grandparent,
    PrMpsvRelationCode.Grandchild,
    PrMpsvRelationCode.SpouseOfBrother_sister,
    PrMpsvRelationCode.PartnerOfBrother_sister,
    PrMpsvRelationCode.Fatherinlaw_Motherinlaw,
    PrMpsvRelationCode.Soninlaw_Daughterinlaw,
    PrMpsvRelationCode.Nephew_Niece,
    PrMpsvRelationCode.Uncle_Aunt,
    PrMpsvRelationCode.SpouseOfFather_mother,
    PrMpsvRelationCode.RegisteredPartnerOfSon_daughter,
    PrMpsvRelationCode.SpouseOfGrandparent,
    PrMpsvRelationCode.SpouseOfGrandchild,
    PrMpsvRelationCode.SpouseOfFatherinlaw_motherinlaw,
    PrMpsvRelationCode.SpouseOfNephew_niece,
    PrMpsvRelationCode.SpouseOfUncle_aunt,
    PrMpsvRelationCode.PartnerOfFather_mother,
    PrMpsvRelationCode.PartnerOfSon_daughter,
    PrMpsvRelationCode.RegisteredPartnerOfGrandparent,
    PrMpsvRelationCode.PartnerOfGrandchild,
    PrMpsvRelationCode.PartnerOfFatherinlaw_motherinlaw,
    PrMpsvRelationCode.PartnerOfNephew_niece,
    PrMpsvRelationCode.PartnerOfUncle_aunt,
    PrMpsvRelationCode.OtherPersonSharingHousehold,
    PrMpsvRelationCode.OtherRelationship,
]);

export const FamilyRelationOrderTransformation = sortItemsByPredefinedOrder([
    PrMpsvFamilyRelationCode.SpouseOfTheInsuredPerson,
    PrMpsvFamilyRelationCode.RelativeInDirectLine,
    PrMpsvFamilyRelationCode.RegisteredPartner,
    PrMpsvFamilyRelationCode.ParentOfTheSpouseOrRegisteredPartner,
    PrMpsvFamilyRelationCode.SiblingOfInsuredPerson,
    PrMpsvFamilyRelationCode.OtherIndividualsInTheSameHousehold,
    PrMpsvFamilyRelationCode.OtherRelationship,

]);

/**
 * Add Care Giving Receiver field definitions, including group definition
 * @param d
 * @param isLongTerm
 */
export function addCaregivingReceiverDefinitions(d: IDefinition, isLongTerm?: boolean): void {
    const BaseEntity = isLongTerm ? PrLongTermCaregivingEventEntity : PrCaregivingEventEntity;

    const personRows = addCareReceiverDefinitions(d, BaseEntity.CareReceiver, true, true);

    const RelationPath = isLongTerm ? PrLongTermCaregivingEventEntity.CareReceiverRelation : PrCaregivingEventEntity.CareReceiverFamilyRelation;
    addFormDef(d, RelationPath, {
        type: FieldType.ComboBox,
        width: BasicInputSizes.XL,
        isRequired: true,
        fieldSettings: {
            displayName: PrMpsvRelationEntity.Name,
            transformFetchedItems: isLongTerm ? MpsvRelationCodeOrderTransformation : FamilyRelationOrderTransformation
        }
    });

    const lastRow = personRows[personRows.length - 1];
    lastRow.push({ id: RelationPath });

    addFormGroup(d, {
        id: SocialEventGroup.CareReceiver,
        title: i18next.t("SocialEvents:Form.CareReceiver"),
        rows: [
            ...personRows
        ]
    });
}

/**
 * Add Child field definitions for Maternity and Paternity Events, including group definition
 * @param d
 * @param BaseEntity
 * @param isMulti
 * @param isVisibleClb
 */
export function addChildDefinitions(d: IDefinition, BaseEntity: typeof PrMaternityLeaveEventEntity | typeof PrPaternityLeaveEventEntity, isMulti: boolean, isVisibleClb: TInfoValue<boolean>): void {
    const personRows = addCareReceiverDefinitions(d, BaseEntity.CareReceivers, false);

    [
        PrCareReceiverSocialEventEntity.FirstName, PrCareReceiverSocialEventEntity.LastName,
        PrCareReceiverSocialEventEntity.IdentificationNumber, PrCareReceiverSocialEventEntity.DateOfBirth, IdentificationTypeLocalPath
    ]
        .forEach(id => enhanceFieldVisibility(d, createPath(PrMaternityLeaveEventEntity.CareReceivers, id), isVisibleClb));

    // Care receiver is collection in both cases, but for paternity leave it is displayed as a single child, however
    // for maternity leave there could be more than one defined - visual is classic collection with add/remove buttons.
    const attachmentsPath = createPath(BaseEntity.CareReceivers, PrCareReceiverSocialEventEntity.Attachments);
    addFormDef(d, attachmentsPath, {
        ...formAttachmentFieldDef,
        label: i18next.t("SocialEvents:Form.BirthCertificateAttachments"),
        textAlign: TextAlign.Left,
        isVisible: isVisibleClb
    });
    personRows[personRows.length - 1].push({ id: PrCareReceiverSocialEventEntity.Attachments });
    d.form.files.readOnlyAttachmentPaths.push(attachmentsPath);

    if (isMulti) {
        // collection group
        const columns = personRows.flat();

        // shorten identification value columns
        [PrCareReceiverEntity.IdentificationNumber, PrCareReceiverEntity.DateOfBirth].forEach(prop => {
            d.form.fieldDefinition[createPath(BaseEntity.CareReceivers, prop)].width = FastEntryInputSizes.S;
        });
        // ungroup Name and Surname columns in collection
        [PrCareReceiverEntity.FirstName, PrCareReceiverEntity.LastName].forEach(prop => {
            delete d.form.fieldDefinition[createPath(BaseEntity.CareReceivers, prop)].groupedField;
        });

        addFormGroup(d, {
            id: SocialEventGroup.Child,
            title: i18next.t("SocialEvents:Form.Children"),
            lineItems: {
                collection: PrMaternityLeaveEventEntity.CareReceivers,
                disableVirtualization: true,
                canAdd: true,
                canReorder: false,
                isItemCloneable: false,
                isTheOnlyItemRemovable: false,
                renderAsFastEntryForReadOnly: true,
                columns,
                order: PrCareReceiverSocialEventEntity.Id
            },
            isVisible: isVisibleClb
        });
    } else {
        // single child group
        addFormGroup(d, {
            id: SocialEventGroup.Child,
            title: i18next.t("SocialEvents:Form.Child"),
            collection: PrPaternityLeaveEventEntity.CareReceivers,
            collectionOrder: PrCareReceiverSocialEventEntity.Id,
            rows: personRows,
            isVisible: isVisibleClb
        });
    }
}

export const basicToggleSwitchDef = {
    type: FieldType.Switch,
    fieldSettings: {
        type: SwitchType.YesNo
    },
    customizationData: {
        useForCustomization: false
    },
};

export const isEndedEvent: TGetValueFn<boolean> = ({ storage }) => !!(storage as FormStorage).data.origEntity?.DateEventEnd;
export const hasInvalidDateStart: TGetValueFn<boolean> = (args) => {
    // toggle should be disabled if DateEventStart is not set or invalid
    const { storage } = args;
    const dateEventStart = storage.getValueByPath(PrSocialEventEntity.DateEventStart);
    return !dateEventStart || !getUtcDayjs(dateEventStart).isValid();
};

export function addBasicMonthDetailDefinitions(d: IDefinition, localEndFieldsPrefix?: string): void {
    const generalGroup = getFormGroup(d, SocialEventGroup.General);
    // ************* General group definitions *************
    const workedHoursDef: IFieldInfoProperties = {
        fieldSettings: {
            unit: getHoursUnit,
            min: 0
        },
        formatter: (val: TValue, args) => (args.readonly ? val ?? DASH_CHARACTER : val)?.toString(),
        validator: {
            type: ValidatorType.Number,
            settings: {
                min: 0
            }
        },
    };
    addFormDef(d, PrSocialEventEntity.WorkedHoursOnFirstDay, {
        ...workedHoursDef
    });
    addFormDef(d, PrSocialEventEntity.WorkedHoursOnLastDay, {
        ...workedHoursDef,
        label: i18next.t("SocialEvents:Form.WorkedHoursOnLastDayShort"),
        isDisabled: isStartAndEndSameDay,
        useForValidation: true,
    });
    addFormDef(d, PrSocialEventEntity.DateEventStart, {
        isRequired: true,
        affectedFields: [
            { id: hasDurationIntervalsLocalPath },
            { id: PrSocialEventEntity.DateEventEnd, revalidate: true }
        ],
        isDisabled: (args) => {
            const { storage } = args;
            return storage.data.entity?.MonthDetails?.length > 0;
        }
    });
    addFormDef(d, PrSocialEventEntity.DateEventEnd, {
        fieldSettings: {
            minDate: getOtherPropertyGetter(PrSocialEventEntity.DateEventStart)
        },
        isRequired: (args) => {
            const { storage } = args;
            return !!(storage as TSocialEventFormStorage).getCustomData()?.isEndDialogOpened;
        },
        useForValidation: true,
    });
    // we need different path to show in form for ended events and different for temporal dialog,
    // so they don't interfere with each other
    addFormDef(d, createPath(localEndFieldsPrefix, dateEventEndLocalPath), {
        isReadOnly: true,
        label: i18next.t("SocialEvents:Form.DateEventEnd"),
        isVisible: isEndedEvent,
        formatter: (_, args) => {
            return DateType.format((args.storage as FormStorage<IPrSocialEventEntity>).data.origEntity.DateEventEnd);
        },
        comparisonFunction: (entity1, entity2) => {
            const date1 = (entity1 as IPrSocialEventEntity).DateEventEnd;
            const date2 = (entity2 as IPrSocialEventEntity).DateEventEnd;
            return isValidDate(date1) && isValidDate(date2) && getUtcDayjs(date1).isSame(getUtcDayjs(date2), "day");
        }
    });
    addFormDef(d, createPath(localEndFieldsPrefix, workedHoursOnLastDayLocalPath), {
        ...workedHoursDef,
        isReadOnly: true,
        label: i18next.t("SocialEvents:Form.WorkedHoursOnLastDay"),
        isVisible: isEndedEvent,
        formatter: (_, args) => {
            const { WorkedHoursOnLastDay } = (args.storage as FormStorage<IPrSocialEventEntity>).data.origEntity;
            return workedHoursDef.formatter(WorkedHoursOnLastDay, args);
        },
        comparisonFunction: (entity1, entity2) => {
            const wh1 = (entity1 as IPrSocialEventEntity).WorkedHoursOnLastDay;
            const wh2 = (entity2 as IPrSocialEventEntity).WorkedHoursOnLastDay;
            return wh1 === wh2;
        },
        fieldSettings: {
            ...workedHoursDef.fieldSettings,
            unit: args => {
                return getHoursUnit({ ...args, bindingContext: args.storage.data.bindingContext.navigate(PrSocialEventEntity.WorkedHoursOnLastDay) });
            }
        }
    });
    generalGroup.rows[generalGroup.rows.length - 1].push(
        { id: PrSocialEventEntity.DateEventStart },
        { id: PrSocialEventEntity.WorkedHoursOnFirstDay }
    );

    // ************* Continue intervals *************
    addFormDef(d, hasDurationIntervalsLocalPath, {
        ...basicToggleSwitchDef,
        isDisabled: hasInvalidDateStart,
        disabledText: i18next.t("SocialEvents:Form.SetDateEventStartToEnable"),
        affectedFields: [
            { id: PrSocialEventEntity.DateEventStart }
        ]
    });
    addFormDef(d, createPath(SocialEventGroup.MonthDetails, PrSocialEventMonthDetailEntity.Month), {
        type: FieldType.Input, // it is DateInput by default, which has fixed Right alignment...
        label: i18next.t("SocialEvents:Form.Month"),
        textAlign: TextAlign.Left,
        formatter: (val: TValue, args) => {
            const { storage, bindingContext } = args;
            const yearBc = bindingContext.getParent().navigate(PrSocialEventMonthDetailEntity.Year);
            const year = storage.getValue(yearBc);
            const date = getUtcDayjs().month(Number(val) - 1).year(year);
            return capitalize(DateType.format(date, DateFormat.monthAndYear));
        },
        isDisabled: true,
        additionalProperties: [
            { id: `${SEPARATOR}${createPath(SocialEventGroup.MonthDetails, PrSocialEventMonthDetailEntity.Year)}` }
        ]
    });

    addFormGroup(d, {
        id: SocialEventGroup.MonthDetails,
        togglePropPath: hasDurationIntervalsLocalPath,
        title: i18next.t("SocialEvents:Form.MonthDetails"),
        lineItems: {
            collection: PrSocialEventEntity.MonthDetails,
            canReorder: false,
            canAdd: ifAll(isDurationGroupToggled, not(isEndedEvent)),
            filterItems: filterOutInvisibleDurationItems,
            isItemCloneable: false,
            unavailableActionStyle: UnavailableActionStyle.Disable,
            isItemRemovable: (args) => {
                // only last item is removable and only if event is not ended. We also don't allow to remove
                // first item as it should be removed with the toggle switch
                if (isEndedEvent(args)) {
                    return false;
                }
                const { storage, bindingContext } = args;
                const baseBc = bindingContext.removeKey();
                const monthDetails = storage.getValue(baseBc) as PrSocialEventMonthDetailEntity[];
                const lastMonthDetail = monthDetails[monthDetails.length - 1];
                return bindingContext.getPath(false) === baseBc.addKey(lastMonthDetail).getPath(false);
            },
            columns: [
                { id: PrSocialEventMonthDetailEntity.Month },
            ],
            order: "Id"
        }
    });
}

export const MonthDetailIntervalsFastEntryDef: IFormGroupDef = {
    id: SocialEventGroup.CaregivingDetailIntervals,
    lineItems: {
        collection: PrSocialEventMonthDetailEntity.Intervals,
        order: PrSocialEventMonthIntervalEntity.Id,
        canReorder: false,
        isItemCloneable: false,
        canAdd: isOfCaregivingDetailType(PrSocialEventMonthDetailTypeCode.OnlyInSelectedDays),
        columns: [
            { id: PrSocialEventMonthIntervalEntity.DateFrom },
            { id: PrSocialEventMonthIntervalEntity.DateTo }
        ]
    }
};

const isStartAndEndSameDay: TGetValueFn<boolean> = ({ storage, bindingContext }) => {
    const entity = storage.data.entity as IPrSocialEventEntity;
    const temporalVal = storage.getTemporalData(storage.data.bindingContext.navigate(PrSocialEventEntity.DateEventEnd))?.value;
    // use temporal value if available, otherwise use entity value as it's used for disabling fields in temporal dialog
    const dateEventEnd = temporalVal ?? entity.DateEventEnd;
    return getUtcDayjs(entity.DateEventStart).isSame(getUtcDayjs(dateEventEnd as Date), "day");
};

export function addCaregivingMonthDetailDefinitions(d: IDefinition): void {
    addBasicMonthDetailDefinitions(d, PrSocialEventEntity.MonthDetails);

    // Custom column for month detail
    addFormDef(d, createPath(PrSocialEventEntity.MonthDetails, PrSocialEventMonthDetailEntity.MonthDetailType), {
        type: FieldType.ComboBox,
        width: BasicInputSizes.XXL,
        label: i18next.t("SocialEvents:Form.CaregivingDetailType"),
        defaultValue: PrSocialEventMonthDetailTypeCode.WholeTimeOfAbsence,
        isDisabled: isStartAndEndSameDay,
        fieldSettings: {
            displayName: PrSocialEventMonthDetailTypeEntity.Name
        },
        additionalProperties: [
            { id: `/${createPath(PrSocialEventEntity.MonthDetails, PrSocialEventMonthDetailEntity.IsEnd)}` }
        ]
    });

    const monthDetailsGroup = getFormGroup(d, SocialEventGroup.MonthDetails);
    monthDetailsGroup.lineItems.columns.push({ id: PrSocialEventMonthDetailEntity.MonthDetailType });
    monthDetailsGroup.lineItems.customRowAfterContent = (args: ICustomRowAfterContentArgs<IPrSocialEventMonthDetailEntity>) => {
        const { storage, item, itemBc, onBlur, onAction, onChange } = args;
        if (item.MonthDetailTypeCode === PrSocialEventMonthDetailTypeCode.OnlyInSelectedDays) {
            const { isReadOnly } = storage;
            return (
                <SmartFastEntrySubListWrapper $readOnly={isReadOnly}>
                    <SmartFastEntryList
                        isReadOnly={isReadOnly}
                        useLabelWrapping={true}
                        groupId={MonthDetailIntervalsFastEntryDef.id}
                        isCollapsible={false}
                        storage={storage}
                        bindingContext={itemBc.navigate(PrSocialEventMonthDetailEntity.Intervals)}
                        canAdd={true}
                        isAddButtonTransparent
                        isTheOnlyItemRemovable={false}
                        isItemCloneable={false}
                        columns={MonthDetailIntervalsFastEntryDef.lineItems.columns}
                        order={null}
                        onBlur={onBlur} onChange={onChange} onAction={onAction}
                    />
                </SmartFastEntrySubListWrapper>
            );
        }
        return null;
    };

    const commonIntervalDefs: Partial<IFieldInfoProperties> = {
        type: FieldType.Date,
        isVisible: isOfCaregivingDetailType(PrSocialEventMonthDetailTypeCode.OnlyInSelectedDays),
        fieldSettings: {
            previewValue: ({ bindingContext, storage }) => {
                const md = storage.getValue(bindingContext.getParent().getParent()) as IPrSocialEventMonthDetailEntity;
                const intervalDate = getMonthIntervalDay(md)?.toDate();
                const previewDate = md.IsEnd ? (storage.data.entity as IPrSocialEventEntity).DateEventEnd : intervalDate;
                return isValidDate(previewDate) ? previewDate : getUtcDayjs().toDate();
            }
        },
        customizationData: {
            useForCustomization: false
        },
        useForValidation: true
    };

    addFormDef(d, createPath(PrSocialEventEntity.MonthDetails, PrSocialEventMonthDetailEntity.Intervals, PrSocialEventMonthIntervalEntity.DateFrom), {
        ...commonIntervalDefs,
        fieldSettings: {
            ...commonIntervalDefs.fieldSettings,
            minDate: args => {
                const { storage, bindingContext } = args;
                const monthBc = bindingContext.getParent().getParent();
                const dates: Dayjs[] = [];
                const monthDate = getMonthIntervalDayStorage(storage, monthBc);
                if (monthDate) {
                    dates.push(monthDate.startOf("month"));
                }
                dates.push(getUtcDayjs(storage.data.entity.DateEventStart));
                // previous interval end date
                const intervalsBc = monthBc.navigate(PrSocialEventMonthDetailEntity.Intervals);
                const intervals = storage.getValue(intervalsBc) as IPrSocialEventMonthIntervalEntity[];
                const currPath = bindingContext.getParent().getPath();
                const currentIntervalIndex = intervals.findIndex(i => intervalsBc.addKey(i).getPath() === currPath);
                if (currentIntervalIndex > 0) {
                    const previousInterval = intervals[currentIntervalIndex - 1];
                    dates.push(getUtcDayjs(previousInterval.DateTo).add(1, "day"));
                }
                // find the latest date to set as minDate
                return dates.reduce((latest, date) => date.isAfter(latest) ? date : latest, getUtcDayjs(DATE_MIN)).toDate();
            },
            maxDate: ({ storage, bindingContext }) => {
                const { DateEventEnd } = (storage.data.entity as IPrSocialEventEntity);
                const monthBc = bindingContext.getParent().getParent();
                const monthDetail = storage.getValue(monthBc) as IPrSocialEventMonthDetailEntity;

                if (monthDetail.IsEnd && isValidDate(DateEventEnd)) {
                    return DateEventEnd;
                }
                return undefined;
            }
        },
        label: i18next.t("SocialEvents:Form.From"),
    });

    addFormDef(d, createPath(PrSocialEventEntity.MonthDetails, PrSocialEventMonthDetailEntity.Intervals, PrSocialEventMonthIntervalEntity.DateTo), {
        ...commonIntervalDefs,
        fieldSettings: {
            ...commonIntervalDefs.fieldSettings,
            minDate: getOtherPropertyGetter(PrSocialEventMonthIntervalEntity.DateFrom),
            maxDate: args => {
                const { storage, bindingContext } = args;
                const monthBc = bindingContext.getParent().getParent();
                const endOfCurrentMonth = getMonthIntervalDayStorage(storage, monthBc)?.endOf("month").toDate();
                const monthDetail = storage.getValue(monthBc) as IPrSocialEventMonthDetailEntity;
                const endOfEvent = (storage.data.entity as IPrSocialEventEntity).DateEventEnd;
                if (monthDetail.IsEnd && isValidDate(endOfEvent)) {
                    return endOfEvent;
                }
                return endOfCurrentMonth;
            }
        },
        affectedFields: [
            { id: PrSocialEventMonthIntervalEntity.DateFrom, navigateFromParent: true },
        ],
        label: i18next.t("SocialEvents:Form.To"),
    });

    // End event group definition - always read only, visible only for ended events. Data are set in dialog using button action.
    addFormGroup(d, {
        id: SocialEventGroup.MonthDetailsEnd,
        title: i18next.t("SocialEvents:Form.MonthDetailsEnd"),
        collection: PrSocialEventEntity.MonthDetails,
        collectionOrder: PrSocialEventMonthDetailEntity.Id,
        collectionKey: (args) => {
            const { storage, bindingContext } = args;
            const monthDetails = storage.getValue(bindingContext) as IPrSocialEventMonthDetailEntity[];
            const endDetail = monthDetails?.find(md => md.IsEnd);
            return endDetail ? endDetail.Id : "";
        },
        rows: [[
            { id: dateEventEndLocalPath },
            { id: workedHoursOnLastDayLocalPath }
        ], [
            { id: PrSocialEventMonthDetailEntity.MonthDetailType }
        ]],
        lineItems: {
            collection: PrSocialEventMonthDetailEntity.Intervals,
            columns: [
                { id: PrSocialEventMonthIntervalEntity.DateFrom },
                { id: PrSocialEventMonthIntervalEntity.DateTo },
            ],
            order: PrSocialEventMonthIntervalEntity.Id
        }
    });
}

export function addSubmissionTableDefinitions(d: IDefinition): void {
    addFormGroup(d, {
        id: "Tabs",
        isVisible: not(isNewEntity),
        tabs: [getSubmissionTableDefinitionForFormTabs(PrSubmissionEntity.SocialEvent)]
    });
}

/**
 * Add Care Employee Declarations field definitions, including group definition
 * @param d
 * @param fieldIds
 */
export function addSwitchGroupDefinitions(d: IDefinition, fieldIds: string[]): void {
    fieldIds.forEach(id => {
        addFormDef(d, id, {
            type: FieldType.Switch,
            label: i18next.t(`SocialEvents:Declarations.${id}`),
            fieldSettings: {
                type: SwitchType.YesNo,
            },
        });
    });

    addFormGroup(d, {
        id: SocialEventGroup.CareEmployeeDeclarations,
        title: i18next.t("SocialEvents:Form.CareEmployeeDeclarations"),
        rows: [
            ...fieldIds.map(id => ([{ id }]))
        ]
    });
}

/**
 * User fills DateEventEnd directly in the form (it's mostly expected DateEventEnd) and the SocialEvent is
 * Ended when the date becomes past - "past" === DateEventEnd fits to closed attendance, therefore user cannot edit it anymore.
 * We keep the form editable until the attendance is closed.
 * @param d
 */
export function addReadOnlyForSelfClosingEvents(d: IDefinition): void {
    d.form.isReadOnly = (args) => {
        const storage = args.storage as FormStorage<IPrSocialEventEntity>;
        const { SocialEventStatusCode, IsProcessed } = storage.data.origEntity ?? {};
        return PrSocialEventStatusCode.Cancelled === SocialEventStatusCode || !!IsProcessed;
    };

    d.form.additionalProperties.push({ id: PrSocialEventEntity.IsProcessed });
}

/**
 * If user uses button to end the event, we set DateEventEnd and expect the form to become read only immediately.
 * We also make it read only if the event is cancelled.
 * @param d
 */
export function addReadOnlyForEventsWithCloseButton(d: IDefinition): void {
    d.form.isReadOnly = ({ storage }) => {
        const statusCode = (storage as FormStorage<IPrSocialEventEntity>).data.origEntity?.SocialEventStatusCode;
        // cancelled from status, ended from DateEventEnd, because status is based on current datetime
        return PrSocialEventStatusCode.Cancelled === statusCode || isEndedEvent({ storage });
    };
}

interface ISocialEventsFormOpts {
    optionalAttachments?: boolean;
}

const isNempriActionAvailableForEventType: TGetValueFn<boolean> = (args) => {
    const { storage } = args;
    const typeCode = (storage as FormStorage<IPrSocialEventEntity>).data.origEntity?.SocialEventTypeCode;
    const submissionSubType = getNempriSubmissionSubTypeFromSocialEventType(typeCode);
    return !!submissionSubType;
};

export const getSocialEventsFormDef = (formControl: ComponentType<any>, type: PrSocialEventTypeCode, context: IAppContext, opts?: ISocialEventsFormOpts): IDefinition => {

    const translationFile = getDefinitions.translationFiles[0];
    const entityType = getSocialEventsEntityType(type);

    const form: IFormDef = {
        id: `${entityType}Form`,
        permissions: [UserPermissionCodes.PrPayroll], // todo: customer permission
        formControl,
        summary: [
            getNumberOursSummaryDef(translationFile),
            getEmployeeSummaryItemDef(PrSocialEventEntity.Employee),
            {
                id: PrSocialEventEntity.SocialEventTypeCode,
                label: i18next.t("SocialEvents:Form.Type"),
                formatter: (val: TValue) => {
                    return getSocialEventTypeLabel(val as PrSocialEventTypeCode);
                },
                updateFromLiveValue: true
            },
            {
                id: PrSocialEventEntity.SocialEventStatusCode,
                label: i18next.t("SocialEvents:Form.Status"),
                formatter: (_, args) => {
                    return getSocialEventStatusLabel((args.storage as FormStorage).data.origEntity);
                },
                colorFormatter: (_, args) => {
                    return getSocialEventStatusColor((args.storage as FormStorage).data.origEntity);
                },
                isVisible: not(isNewEntity),
                additionalProperties: [
                    { id: `/${createPath(PrSocialEventEntity.MonthDetails, PrSocialEventMonthDetailEntity.Month)}` },
                    { id: `/${createPath(PrSocialEventEntity.MonthDetails, PrSocialEventMonthDetailEntity.Year)}` }
                ]
            }
        ],
        customHeaderActions: [{
            id: PrSubmissionTypeCode.EmployersNotificationOfAnEmployeesBenefitApplication,
            label: i18next.t("SocialEvents:Form.CreateNEMPRI"),
            iconName: "NEMPRI",
            isVisible: ifAll(not(isNewEntity), isNempriActionAvailableForEventType, isAllowedToCreateSubmission),
            isDisabled: false
        }],
        ignoreVariants: true,
        additionalProperties: [
            { id: PrSocialEventEntity.SocialEventStatusCode },
        ],
        files: {
            ...AttachmentsDef,
            enabledInReadOnlyMode: true,
            readOnlyAttachmentPaths: [
                PrSocialEventEntity.CertificateAttachments
            ]
        },
        translationFiles: getDefinitions.translationFiles,
        isDeletable: true,
        getItemBreadCrumbText: (storage: Model) =>
                getItemBreadCrumbsText(storage, seT(context, "NewFormTitle"), storage.data.entity?.NumberOurs),
        title: seT(context, "Form.Title"),
        fieldDefinition: {
            ...getNumberRangeFieldDefs(translationFile),
            [PrSocialEventEntity.Employee]: {
                ...employeeWithDependentEmploymentSelectDef,
                isVisible: ifAll(employeeWithDependentEmploymentSelectDef.isVisible, canChangeEmployee),
            },
            [PrSocialEventEntity.Employment]: getEmploymentSelectDef(PrSocialEventEntity.Employee),
            [PrSocialEventEntity.NumberTheirs]: {
                label: seT(context, "Form.NumberTheirs"),
                width: BasicInputSizes.XL,
                isRequired: true
            },
            [PrSocialEventEntity.CertificateAttachments]: {
                ...formAttachmentFieldDef,
                label: seT(context, "Form.CertificateAttachments"),
            }
        },
        groups: [{
            id: SocialEventGroup.General,
            rows: [[
                { id: PrSocialEventEntity.Employee },
                { id: PrSocialEventEntity.Employment },
            ], [
                { id: PrSocialEventEntity.NumberTheirs },
                ...(opts?.optionalAttachments ? [] : [{ id: PrSocialEventEntity.CertificateAttachments }])
            ]]
        }]
    };

    const def = {
        form
    };

    // this is default for all social events, could be overridden in specific social event form definition
    addReadOnlyForSelfClosingEvents(def);

    return def;
};

function formatRequiredAction(entity: IPrSocialEventEntity): string {
    const { DateRequiredActionFrom, RequiredAction } = entity;
    const now = getUtcDayjs();
    if ((DateRequiredActionFrom && now.isBefore(DateRequiredActionFrom)) || !RequiredAction?.Code) {
        return DASH_CHARACTER;
    }
    return RequiredAction?.Name;
}

export function addRequiredActionSummaryDef(d: IDefinition): void {
    d.form.summary.push({
        id: PrSocialEventEntity.RequiredAction,
        label: i18next.t("SocialEvents:Summary.RequiredAction"),
        formatter: (_, args) => {
            const entity = args.storage.data.entity as IPrSocialEventEntity;
            return formatRequiredAction(entity);
        },
        isVisible: not(isNewEntity),
        additionalProperties: [
            { id: `/${PrSocialEventEntity.DateRequiredActionFrom}` },
        ]
    });
}

export const getDefinitions: IGetDefinition = (context: IAppContext): IDefinition => {
    const filterBarDef: IFilterGroupDef[] = [{
        ...getDefaultFilterGroupDef(FilterBarGroup.Filters),
        isValueHelp: true,
        defaultFilters: [
            PrSocialEventEntity.SocialEventType,
            PrSocialEventEntity.Employee,
            createPath(PrSocialEventEntity.Employment, PrEmploymentEntity.NumberOurs),
        ],
        filterDefinition: {
            ...withDisplayName(PrSocialEventEntity.SocialEventType),
            ...getEmployeeFilterDef(PrSocialEventEntity.Employee),
            ...getCommonFilterDefs(),
            [createPath(PrSocialEventEntity.Employment, PrEmploymentEntity.NumberOurs)]: {}
        }
    }];

    const table: ISplitPageTableDef = {
        filterBarDef,
        id: `${EntityTypeName.PrSocialEvent}Table`,
        massEditableDef: {},
        initialSortBy: [
            { id: PrSocialEventEntity.NumberOurs, sort: Sort.Desc }
        ],
        additionalProperties: [
            { id: PrSocialEventEntity.DateRequiredActionFrom }
        ],
        columns: [
            PrSocialEventEntity.NumberOurs,
            PrSocialEventEntity.Employee,
            createPath(PrSocialEventEntity.Employment, PrEmploymentEntity.NumberOurs),
            PrSocialEventEntity.SocialEventType,
            PrSocialEventEntity.SocialEventStatus,
            PrSocialEventEntity.RequiredAction,
            PrSocialEventEntity.DateCreated,
        ],
        columnDefinition: {
            [PrSocialEventEntity.NumberOurs]: {},
            ...getEmployeeTableColumnDefs(PrSocialEventEntity.Employee),
            ...withDisplayName(PrSocialEventEntity.SocialEventType, {
                label: i18next.t("SocialEvents:Table.Type"),
                formatter: (_: TValue, { item }) => {
                    const code = (item as IPrSocialEventEntity)?.SocialEventType?.Code;
                    return getSocialEventTypeLabel(code);
                },
            }),
            [createPath(PrSocialEventEntity.Employment, PrEmploymentEntity.NumberOurs)]: {
                label: i18next.t("SocialEvents:Table.Employment"),
                formatter: (val, { storage, entity }: IFormatOptions<IPrSocialEventEntity>) => {
                    const { Id } = entity.Employment;
                    return getTableIntentLink(val as string, {
                        route: `${ROUTE_EMPLOYMENT}/${Id}`,
                        context: storage.context,
                        storage
                    });
                }
            },
            [PrSocialEventEntity.DateCreated]: {
                label: i18next.t("SocialEvents:Table.DateCreated"),
            },
            ...withDisplayName(PrSocialEventEntity.RequiredAction, {
                formatter: (_, args): string => {
                    const entity = args.item as IPrSocialEventEntity;
                    return formatRequiredAction(entity);
                }
            }),
            ...withDisplayName(PrSocialEventEntity.SocialEventStatus, {
                label: i18next.t("SocialEvents:Table.Status"),
                formatter: (_, args): ICellValueObject => {
                    const entity = args.item as IPrSocialEventEntity;
                    const statusLabel = getSocialEventStatusLabel(entity, true);
                    return {
                        tooltip: statusLabel,
                        value: (
                            <ColoredText color={getSocialEventStatusColor(entity)}>
                                {statusLabel}
                            </ColoredText>
                        )
                    };
                },
                additionalProperties: [
                    { id: `/${createPath(PrSocialEventEntity.MonthDetails, PrSocialEventMonthDetailEntity.Year)}` },
                    { id: `/${createPath(PrSocialEventEntity.MonthDetails, PrSocialEventMonthDetailEntity.Month)}` }
                ]
            }),
            [PrSocialEventEntity.DateEventStart]: {},
            [PrSocialEventEntity.DateEventEnd]: {},
        },
        title: seT(context, "Table.Title"),
        tabs: [
            {
                id: SocialEventTabStatus.Active,
                title: i18next.t("SocialEvents:Table.Active")
            },
            {
                id: SocialEventTabStatus.Processed,
                title: i18next.t("SocialEvents:Table.Processed")
            },
            {
                id: SECONDARY_FILTERS_ALL,
                title: i18next.t("SocialEvents:Table.All"),
            },
        ],
        tabsSettings: {
            customFilter: getFilterForTabId(context)
        },
    };

    if (!canChangeEmployee({ context })) {
        // user is Employee - hide Employee column
        table.columns = table.columns.filter(c => c !== PrSocialEventEntity.Employee);
        if (table.columnDefinition[PrSocialEventEntity.Employee]) {
            delete table.columnDefinition[PrSocialEventEntity.Employee];
        }
        // and also filter by Employee
        table.filterBarDef[0].defaultFilters = table.filterBarDef[0].defaultFilters.filter(f => f !== PrSocialEventEntity.Employee);
        if (table.filterBarDef[0].filterDefinition[PrSocialEventEntity.Employee]) {
            delete table.filterBarDef[0].filterDefinition[PrSocialEventEntity.Employee];
        }
        // filter by current user
        table.filter = getSingleEmployeeSocialEventFilter(context);
    }

    return {
        entitySet: EntitySetName.PrSocialEvents,
        table
    };
};

getDefinitions.translationFiles = [
    "SocialEvents", "SocialRequests", "Employee", "Submission",
    "Common", "Components", "NumberRange", "Error", "Enums",
    getEnumNameSpaceName(EntityTypeName.PrSocialEventType),
    getEnumNameSpaceName(EntityTypeName.PrSocialEventStatus),
    ...PrSubmissionEnumTranslationNamespaces
];
setDefByEntityType(EntityTypeName.PrSocialEvent, getDefinitions);
